/**
 * 智能群发消息插件 - 主组件
 */
import './modern-theme.css';
import React, { useState, useEffect } from 'react';
import { initView } from 'dingtalk-docs-cool-app';
import { 
  Typography, 
  Button, 
  Card, 
  Steps,
  message,
  Space,
  Spin,
} from 'dingtalk-design-desktop';

import { BusinessKeySelector } from './BusinessKeySelector';
import { FieldSelector } from './FieldSelector';
import { MessageEditor } from './MessageEditor';
import { MessagePreview } from './MessagePreview';
import MessageSendPreview from './MessageSendPreview';

import { useBusinessKeys } from '../hooks/useBusinessKeys';
import { useTableFields } from '../hooks/useTableFields';
import { useTableRecords } from '../hooks/useTableRecords';
import { useGroupMatching } from '../hooks/useGroupMatching';
import { useMessageSender } from '../hooks/useMessageSender';

import './style.css';

const { Title, Text } = Typography;
const { Step } = Steps;

function App() {
  // 状态管理
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedFieldIds, setSelectedFieldIds] = useState<string[]>([]);
  const [messageTemplate, setMessageTemplate] = useState('');
  const [atAll, setAtAll] = useState(false);
  const [isPermissionReady, setIsPermissionReady] = useState(false);

  // Hooks - 传入权限状态，等待权限就绪后再加载数据
  const {
    businessKeys,
    selectedBusinessKey,
    receivers,
    loading: businessKeysLoading,
    error: businessKeysError,
    selectBusinessKey,
  } = useBusinessKeys(isPermissionReady);

  // 先加载记录
  const {
    records,
    loading: recordsLoading,
    error: recordsError,
  } = useTableRecords(isPermissionReady);

  // 从记录中提取字段（性能优化：避免额外的 Script 调用）
  const {
    fields,
    loading: fieldsLoading,
    error: fieldsError,
  } = useTableFields(records);

  const {
    matchingResult,
    isMatching,
    performMatching,
    clearMatching,
  } = useGroupMatching();

  const {
    isSending,
    sendProgress,
    sendMessages,
  } = useMessageSender();

  // 初始化鉴权配置
  const initConfigPermission = async () => {
    try {
      // 获取当前页面的URL
      const currentUrl = window.location.href;

      // 从后端服务器请求钉钉配置权限
      // 生产环境：使用相对路径 /api/configPermission（通过 Nginx 代理）
      // 开发环境：使用完整 URL http://localhost:3001/api/configPermission
      const apiUrl = process.env.NODE_ENV === 'production' 
        ? `/api/configPermission?url=${encodeURIComponent(currentUrl)}`
        : `http://localhost:3001/api/configPermission?url=${encodeURIComponent(currentUrl)}`;
      
      const response = await fetch(apiUrl);
      if (!response.ok) {
        throw new Error(`鉴权请求失败: ${response.status} ${response.statusText}`);
      }

      const configData = await response.json();

      // 调用钉钉配置方法
      // @ts-ignore - Dingdocs 类型定义
      await Dingdocs.base.host.configPermission(
        configData.agentId,
        configData.corpId,
        configData.timeStamp,
        configData.nonceStr,
        configData.signature,
        configData.jsApiList || ["DingdocsScript.base.readWriteAll"],
      );

      setIsPermissionReady(true);
    } catch (error) {
      console.error('❌ 权限配置失败:', error);
      // 无论成功失败都设置为就绪，让页面继续渲染（否则会永远卡住）
      setIsPermissionReady(true);
      
      if (process.env.NODE_ENV === 'production') {
        message.error('插件鉴权失败，部分功能可能无法使用');
      } else {
        console.warn('⚠️ 开发环境鉴权失败 - 这可能影响某些功能，但不影响开发');
      }
    }
  };

  // 初始化视图
  useEffect(() => {
    initView({
      onReady: async () => {
        // 初始化插件鉴权（企业内插件/三方企业插件场景必需）
        await initConfigPermission();
      },
    });
  }, []);

  // 错误处理
  useEffect(() => {
    if (businessKeysError) {
      message.error(businessKeysError);
    }
    if (fieldsError) {
      message.error(fieldsError);
    }
    if (recordsError) {
      message.error(recordsError);
    }
  }, [businessKeysError, fieldsError, recordsError]);

  const handleBusinessKeySelect = async (businessKey: any) => {
    await selectBusinessKey(businessKey);
    clearMatching();
    setCurrentStep(1);
  };

  const handleStartMatching = () => {
    if (!selectedBusinessKey) {
      message.warning('请先选择业务标识');
      return;
    }
    if (selectedFieldIds.length === 0) {
      message.warning('请至少选择一个匹配字段');
      return;
    }
    if (receivers.length === 0) {
      message.warning('未找到消息接收者，请检查配置');
      return;
    }
    if (records.length === 0) {
      message.warning('当前表格没有记录');
      return;
    }
    try {
      performMatching(records, receivers, selectedFieldIds);
      setCurrentStep(2);
      message.success('匹配完成');
    } catch (error) {
      message.error('匹配失败，请重试');
    }
  };

  const handleContinueToEdit = () => {
    if (!matchingResult || matchingResult.matchedCount === 0) {
      message.warning('没有匹配成功的记录');
      return;
    }
    setCurrentStep(3);
  };

  // 步骤四“发送消息”按钮，进入步骤五
  const handleGoToSendPreview = () => {
    if (!matchingResult) {
      message.warning('请先执行匹配');
      return;
    }
    if (!messageTemplate.trim()) {
      message.warning('请输入消息内容');
      return;
    }
    setCurrentStep(4);
  };

  const handleReset = () => {
    clearMatching();
    setSelectedFieldIds([]);
    setMessageTemplate('');
    setAtAll(false);
    setCurrentStep(0);
  };

  const isLoading = businessKeysLoading || fieldsLoading || recordsLoading;

  return (
    <div className="app-wrapper">
      <div className="app-container">
        <div className="app-header" style={{ textAlign: 'center' }}>
        {/* 标题居中显示 */}
        <Title level={4} style={{ fontWeight: 600, fontSize: '15px', marginBottom: 2 }}>智能群发消息插件</Title>
        {currentStep === 0 && (
          <Text type="secondary" style={{ fontSize: '0.98rem' }}>根据业务标识自动匹配消息接收者，批量发送个性化消息</Text>
        )}
      </div>
      {/* 极简自定义分步条 */}
      <div className="custom-steps" style={{ display: 'flex', alignItems: 'center', margin: '0 0 16px 0', userSelect: 'none' }}>
        {['选择业务标识', '配置匹配规则', '查看匹配结果', '编辑消息', '发送'].map((label, idx, arr) => (
          <React.Fragment key={label}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 10px',
                height: 26,
                lineHeight: '26px',
                borderRadius: 13,
                background: currentStep === idx ? '#1677ff' : '#f2f4f8',
                color: currentStep === idx ? '#fff' : '#222',
                fontWeight: currentStep === idx ? 600 : 400,
                fontSize: '0.92rem',
                boxShadow: currentStep === idx ? '0 2px 8px #1677ff22' : 'none',
                transition: 'all 0.2s',
                border: currentStep === idx ? '1.2px solid #1677ff' : '1.2px solid #f2f4f8',
                zIndex: 1,
              }}
            >
              {label}
            </div>
            {idx < arr.length - 1 && (
              <div style={{ width: 22, height: 2, background: '#e5e6eb', margin: '0 1px', borderRadius: 1 }} />
            )}
          </React.Fragment>
        ))}
      </div>
      <Spin spinning={isLoading}>
        <div className="app-content">
          <Card title=" 步骤1：选择业务标识" className="step-card" style={{ marginBottom: '16px', border: '1.5px solid #e5e6eb', boxShadow: '0 2px 12px #0001', borderRadius: 12 }}>
            <BusinessKeySelector businessKeys={businessKeys} selectedBusinessKey={selectedBusinessKey} onSelect={handleBusinessKeySelect} loading={businessKeysLoading} />
            {selectedBusinessKey && (
              <div className="selected-info"> 已选择：{selectedBusinessKey.name}<br /> 已加载 <span style={{ color: '#1677ff', fontWeight: 600 }}>{receivers.length}</span> 个消息接收者</div>
            )}
          </Card>
          {selectedBusinessKey && (
            <Card title=" 步骤2：配置匹配规则" className="step-card" style={{ marginBottom: '16px', border: '1.5px solid #e5e6eb', boxShadow: '0 2px 12px #0001', borderRadius: 12 }}>
              <FieldSelector fields={fields} selectedFieldIds={selectedFieldIds} onSelectionChange={setSelectedFieldIds} disabled={currentStep > 2} />
              <div className="step-actions"><Button type="primary" onClick={handleStartMatching} loading={isMatching} disabled={selectedFieldIds.length === 0 || receivers.length === 0 || currentStep > 2}>{isMatching ? '匹配中...' : '开始匹配'}</Button></div>
            </Card>
          )}
          {matchingResult && (
            <Card title=" 步骤3：查看匹配结果" className="step-card" style={{ marginBottom: '16px', border: '1.5px solid #e5e6eb', boxShadow: '0 2px 12px #0001', borderRadius: 12 }}>
              <MessagePreview matchingResult={matchingResult} fields={fields} />
              <div className="step-actions"><Space><Button onClick={handleReset}>重新开始</Button><Button type="primary" onClick={handleContinueToEdit} disabled={matchingResult.matchedCount === 0}>继续编辑消息</Button></Space></div>
            </Card>
          )}
          {currentStep === 3 && matchingResult && (
            <Card title=" 步骤4：编辑消息内容" className="step-card" style={{ marginBottom: '16px', border: '1.5px solid #e5e6eb', boxShadow: '0 2px 12px #0001', borderRadius: 12 }}>
              <MessageEditor messageTemplate={messageTemplate} onTemplateChange={setMessageTemplate} fields={fields} atAll={atAll} onAtAllChange={setAtAll} disabled={isSending} />
              <div className="step-actions"><Space><Button onClick={() => setCurrentStep(2)}>返回修改匹配</Button><Button type="primary" onClick={handleGoToSendPreview} disabled={!messageTemplate.trim()}>预览并发送</Button></Space></div>
            </Card>
          )}
          {currentStep === 4 && matchingResult && (
            <Card title=" 步骤5：发送消息预览" className="step-card" style={{ marginBottom: '16px', border: '1.5px solid #e5e6eb', boxShadow: '0 2px 12px #0001', borderRadius: 12 }}>
              <MessageSendPreview matchingResult={matchingResult} fields={fields} messageTemplate={messageTemplate} />
              <div className="step-actions"><Space><Button onClick={() => setCurrentStep(3)}>返回编辑消息</Button><Button type="primary" onClick={handleReset}>重新开始</Button></Space></div>
            </Card>
          )}
        </div>
      </Spin>
      </div>
    </div>
  );
}

export default App;
